import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight, Download, Eye, EyeOff, Filter, Layers, Search } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';

type SortDirection = 'asc' | 'desc';
type Align = 'left' | 'center' | 'right';

export type FlexibleDataGridColumn<T> = {
  key: string;
  label: string;
  width?: number;
  align?: Align;
  sortable?: boolean;
  visibleByDefault?: boolean;
  render?: (row: T) => ReactNode;
  getValue?: (row: T) => unknown;
  className?: string;
};

export type FlexibleDataGridGroupOption<T> = {
  key: string;
  label: string;
  getValue: (row: T) => string;
};

type FlexibleDataGridProps<T> = {
  title?: string;
  rows: T[];
  columns: FlexibleDataGridColumn<T>[];
  getRowKey: (row: T) => string;
  loading?: boolean;
  emptyMessage?: string;
  searchable?: boolean;
  selectable?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  groupByOptions?: FlexibleDataGridGroupOption<T>[];
  initialGroupByKey?: string;
  rowActions?: (row: T) => ReactNode;
  onSelectionChange?: (rows: T[]) => void;
  className?: string;
};

const normalizeSearchText = (value: unknown) => String(value ?? '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i')
  .replace(/ğ/g, 'g')
  .replace(/ü/g, 'u')
  .replace(/ş/g, 's')
  .replace(/ö/g, 'o')
  .replace(/ç/g, 'c')
  .replace(/\s+/g, ' ')
  .trim();

export function FlexibleDataGrid<T>({
  title,
  rows,
  columns,
  getRowKey,
  loading = false,
  emptyMessage = 'Kayıt bulunamadı.',
  searchable = true,
  selectable = true,
  pageSizeOptions = [25, 50, 100],
  initialPageSize = 25,
  groupByOptions = [],
  initialGroupByKey = 'none',
  rowActions,
  onSelectionChange,
  className = '',
}: FlexibleDataGridProps<T>) {
  const defaultVisibleColumns = useMemo(
    () => columns.filter((column) => column.visibleByDefault !== false).map((column) => column.key),
    [columns]
  );

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(defaultVisibleColumns);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [groupBy, setGroupBy] = useState(initialGroupByKey);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);

  useEffect(() => {
    setVisibleColumns(defaultVisibleColumns);
  }, [defaultVisibleColumns]);

  const searchableRows = useMemo(() => rows.map((row) => {
    const text = columns
      .map((column) => {
        const value = column.getValue ? column.getValue(row) : (row as any)[column.key];
        return normalizeSearchText(value);
      })
      .join(' ');
    return { row, rowKey: getRowKey(row), searchText: text };
  }), [rows, columns, getRowKey]);

  const filteredRows = useMemo(() => {
    const q = normalizeSearchText(search);
    if (!q) return searchableRows;
    return searchableRows.filter((item) => item.searchText.includes(q));
  }, [search, searchableRows]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const column = columns.find((item) => item.key === sortKey);
    if (!column) return filteredRows;
    return [...filteredRows].sort((left, right) => {
      const leftValue = column.getValue ? column.getValue(left.row) : (left.row as any)[sortKey];
      const rightValue = column.getValue ? column.getValue(right.row) : (right.row as any)[sortKey];
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return sortDirection === 'asc' ? leftValue - rightValue : rightValue - leftValue;
      }
      const result = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), 'tr-TR', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? result : -result;
    });
  }, [columns, filteredRows, sortDirection, sortKey]);

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: '', rows: sortedRows }];
    const option = groupByOptions.find((item) => item.key === groupBy);
    if (!option) return [{ key: 'all', label: '', rows: sortedRows }];
    const map = new Map<string, typeof sortedRows>();
    for (const item of sortedRows) {
      const label = option.getValue(item.row) || '-';
      if (!map.has(label)) map.set(label, []);
      map.get(label)?.push(item);
    }
    return Array.from(map.entries()).map(([key, groupRows]) => ({ key, label: key, rows: groupRows }));
  }, [groupBy, groupByOptions, sortedRows]);

  useEffect(() => {
    setCollapsedGroups((current) => {
      const next = { ...current };
      for (const group of groupedRows) {
        if (!(group.key in next)) next[group.key] = true;
      }
      return next;
    });
  }, [groupedRows]);

  const flatItems = useMemo(() => groupedRows.flatMap((group) => {
    if (groupBy === 'none') return group.rows;
    const isCollapsed = collapsedGroups[group.key] ?? true;
    return isCollapsed ? [{ __group: true, group }] : [{ __group: true, group }, ...group.rows];
  }), [collapsedGroups, groupBy, groupedRows]);

  const totalPages = Math.max(1, Math.ceil(flatItems.length / pageSize));
  const paginatedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return flatItems.slice(start, start + pageSize);
  }, [flatItems, page, pageSize, totalPages]);

  const visiblePageRows = useMemo(
    () => paginatedItems.filter((item: any) => !item.__group),
    [paginatedItems]
  );

  const allVisibleSelected = visiblePageRows.length > 0
    && visiblePageRows.every((item: any) => selectedKeys.includes(item.rowKey));

  useEffect(() => {
    const selected = searchableRows.filter((item) => selectedKeys.includes(item.rowKey)).map((item) => item.row);
    onSelectionChange?.(selected);
  }, [onSelectionChange, searchableRows, selectedKeys]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, search, sortKey, sortDirection, groupBy]);

  const activeColumns = useMemo(
    () => columns.filter((column) => visibleColumns.includes(column.key)),
    [columns, visibleColumns]
  );

  const toggleSort = (key: string) => {
    const column = columns.find((item) => item.key === key);
    if (!column?.sortable) return;
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }
    setSortKey(null);
    setSortDirection('asc');
  };

  const toggleVisibleRows = () => {
    const pageKeys = visiblePageRows.map((item: any) => item.rowKey);
    setSelectedKeys((current) => allVisibleSelected
      ? current.filter((key) => !pageKeys.includes(key))
      : [...new Set([...current, ...pageKeys])]);
  };

  const renderSortIcon = (key: string) => {
    if (sortKey !== key) return <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;
  };

  const exportExcel = () => {
    const headerStyle = {
      font: { bold: true, sz: 11, name: 'Calibri', color: { rgb: '1E293B' } },
      fill: { patternType: 'solid' as const, fgColor: { rgb: 'F1F5F9' } },
      alignment: { vertical: 'center' as const, horizontal: 'left' as const },
      border: { bottom: { style: 'thin' as const, color: { rgb: 'CBD5E1' } } },
    };
    const cellStyle = {
      font: { sz: 10, name: 'Calibri', color: { rgb: '1E293B' } },
      alignment: { vertical: 'center' as const },
      border: { bottom: { style: 'thin' as const, color: { rgb: 'E2E8F0' } } },
    };
    const altCellStyle = {
      ...cellStyle,
      fill: { patternType: 'solid' as const, fgColor: { rgb: 'F8FAFC' } },
    };
    const numericStyle = {
      ...cellStyle,
      alignment: { vertical: 'center' as const, horizontal: 'right' as const },
      numFmt: '#,##0.00',
    };
    const numericAltStyle = {
      ...altCellStyle,
      alignment: { vertical: 'center' as const, horizontal: 'right' as const },
      numFmt: '#,##0.00',
    };

    const headers = activeColumns.map((c) => ({ v: c.label, t: 's' as const, s: headerStyle }));
    const bodyRows = sortedRows.map((item, rowIndex) => {
      const row = item.row as T;
      return activeColumns.map((column) => {
        const rawValue = column.getValue ? column.getValue(row) : (row as any)[column.key];
        let cellValue: string | number;
        if (column.format) {
          cellValue = String(column.format(rawValue, row) ?? '-');
        } else {
          const num = Number(rawValue);
          cellValue = rawValue != null && rawValue !== '' && Number.isFinite(num) ? num : String(rawValue ?? '-');
        }
        const isAlt = rowIndex % 2 === 1;
        const isNum = typeof cellValue === 'number';
        const style = isNum ? (isAlt ? numericAltStyle : numericStyle) : (isAlt ? altCellStyle : cellStyle);
        return { v: cellValue, t: isNum ? 'n' as const : 's' as const, s: style };
      });
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...bodyRows]);
    ws['!cols'] = activeColumns.map((column) => ({ wch: Math.max(column.label.length, Math.floor((column.width || 120) / 7)) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title || 'Veriler');
    XLSX.writeFile(wb, `${(title || 'veriler').toLowerCase().replace(/[^a-z0-9ğüşıöç]/gi, '-')}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {(title || searchable || groupByOptions.length > 0) ? (
        <div className="flex flex-wrap items-center gap-3">
          {title ? <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{title}</div> : null}
          {searchable ? (
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Ara..." className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100" />
            </div>
          ) : null}
          <button type="button" onClick={() => setShowColumnPanel((current) => !current)} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
            {showColumnPanel ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Sütunlar
          </button>
          {groupByOptions.length > 0 ? (
            <button type="button" onClick={() => setShowGrouping((current) => !current)} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-[#171717] dark:text-slate-200">
              <Layers className="h-4 w-4" /> Gruplandır
            </button>
          ) : null}
          {filteredRows.length > 0 ? (
            <button type="button" onClick={exportExcel} className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/60">
              <Download className="h-4 w-4" /> Excel
            </button>
          ) : null}
        </div>
      ) : null}

      {showGrouping && groupByOptions.length > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-[#151515]">
          <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Grup Alanı</label>
          <select value={groupBy} onChange={(event) => setGroupBy(event.target.value)} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
            <option value="none">Gruplama Yok</option>
            {groupByOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
          </select>
        </div>
      ) : null}

      {showColumnPanel ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-[#151515]">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {columns.map((column) => {
              const visible = visibleColumns.includes(column.key);
              return (
                <button key={column.key} type="button" onClick={() => setVisibleColumns((current) => visible ? current.filter((key) => key !== column.key) : [...current, column.key])} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm ${visible ? 'border-orange-200 bg-white text-slate-800 dark:border-orange-900/70 dark:bg-[#111111] dark:text-slate-100' : 'border-slate-200 bg-white/70 text-slate-500 dark:border-slate-800 dark:bg-[#101010] dark:text-slate-500'}`}>
                  {visible ? <Eye className="h-4 w-4 shrink-0" /> : <EyeOff className="h-4 w-4 shrink-0" />}
                  <span className="truncate">{column.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#111111]">
        <div className="overflow-auto" style={{ maxHeight: '65vh' }}>
          <table className="min-w-full border-collapse text-[13px]">
            <thead className="sticky top-0 z-20 bg-slate-100 text-slate-700 dark:bg-[#181818] dark:text-slate-200">
              <tr>
                {selectable ? (
                  <th className="sticky left-0 z-20 whitespace-nowrap border-b border-slate-200 bg-slate-100 px-2 py-1.5 text-left font-bold dark:border-slate-800 dark:bg-[#181818]">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleRows} />
                  </th>
                ) : null}
                <th className="whitespace-nowrap border-b border-slate-200 px-2 py-1.5 text-left font-bold dark:border-slate-800">#</th>
                {activeColumns.map((column) => (
                  <th key={column.key} style={{ minWidth: column.width ? `${column.width}px` : undefined }} className={`whitespace-nowrap border-b border-slate-200 px-2 py-1.5 font-bold dark:border-slate-800 ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}`}>
                    <button type="button" onClick={() => toggleSort(column.key)} className="inline-flex items-center gap-1 rounded-lg px-1 py-0.5 text-left hover:bg-slate-200 dark:hover:bg-[#242424]">
                      <span>{column.label}</span>
                      {column.sortable === false ? null : renderSortIcon(column.key)}
                    </button>
                  </th>
                ))}
                {rowActions ? <th className="whitespace-nowrap border-b border-slate-200 px-2 py-1.5 text-left font-bold dark:border-slate-800">İşlemler</th> : null}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={activeColumns.length + (selectable ? 2 : 1) + (rowActions ? 1 : 0)} className="px-4 py-10 text-center text-sm font-medium text-slate-500 dark:text-slate-400">Veriler yükleniyor...</td>
                </tr>
              ) : paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={activeColumns.length + (selectable ? 2 : 1) + (rowActions ? 1 : 0)} className="px-4 py-10 text-center text-sm font-medium text-slate-500 dark:text-slate-400">{emptyMessage}</td>
                </tr>
              ) : paginatedItems.map((item: any, index) => {
                if (item.__group) {
                  const group = item.group;
                  const isCollapsed = collapsedGroups[group.key] ?? true;
                  return (
                    <tr key={`group-${group.key}-${index}`} className="cursor-pointer bg-slate-100 dark:bg-[#1c1c1c]" onClick={() => setCollapsedGroups((current) => ({ ...current, [group.key]: !(current[group.key] ?? true) }))}>
                      <td colSpan={activeColumns.length + (selectable ? 2 : 1) + (rowActions ? 1 : 0)} className="px-3 py-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-300 bg-white text-[11px] dark:border-slate-700 dark:bg-[#171717]">{isCollapsed ? '+' : '-'}</span>
                          <Filter className="h-4 w-4" /> {group.label} ({group.rows.length})
                        </span>
                      </td>
                    </tr>
                  );
                }

                const row = item.row as T;
                const rowKey = item.rowKey as string;
                const checked = selectedKeys.includes(rowKey);
                return (
                  <tr key={rowKey} className="border-b border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/40">
                    {selectable ? (
                      <td className="sticky left-0 z-10 whitespace-nowrap border-r border-slate-200 bg-white px-2 py-1 dark:border-slate-800 dark:bg-[#111111]">
                        <input type="checkbox" checked={checked} onChange={() => setSelectedKeys((current) => current.includes(rowKey) ? current.filter((key) => key !== rowKey) : [...current, rowKey])} />
                      </td>
                    ) : null}
                    <td className="whitespace-nowrap px-2 py-1 text-[12px] font-semibold text-slate-500 dark:text-slate-400">{(page - 1) * pageSize + index + 1}</td>
                    {activeColumns.map((column) => {
                      const value = column.render ? column.render(row) : String((column.getValue ? column.getValue(row) : (row as any)[column.key]) ?? '-');
                      return (
                        <td key={`${rowKey}-${column.key}`} className={`px-2 py-1 text-[13px] text-slate-800 dark:text-slate-100 ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'} ${column.className || ''}`}>
                          {value}
                        </td>
                      );
                    })}
                    {rowActions ? <td className="px-2 py-1">{rowActions(row)}</td> : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-[#141414]">
          <div className="text-sm text-slate-600 dark:text-slate-400">{filteredRows.length} kayıt</div>
          <div className="flex items-center gap-3">
            <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none dark:border-slate-700 dark:bg-[#171717] dark:text-slate-100">
              {pageSizeOptions.map((option) => <option key={option} value={option}>{option} kayıt</option>)}
            </select>
            <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1} className="rounded-lg border border-slate-300 px-2 py-1 disabled:opacity-50 dark:border-slate-700">
                <ChevronRight className="h-4 w-4 rotate-180" />
              </button>
              <span>{page} / {totalPages}</span>
              <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages} className="rounded-lg border border-slate-300 px-2 py-1 disabled:opacity-50 dark:border-slate-700">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlexibleDataGrid;
